import { EXISTING_LAYER_ID, IkeToken, type VariantLayerNode } from '@ikegps/core-api';
import { action, makeObservable, observable, type ObservableMap } from 'mobx';
import { Services } from '../services';
import { AppStore } from '../store';

/** A layer plus its indentation depth (root = 0), for rendering the tree as an indented menu. */
export interface FlatVariantLayer {
	node: VariantLayerNode;
	depth: number;
}

/**
 * Tracks which variant layer is selected for each job. Layers are never stored: the tree is derived
 * on the server (`tagSummary.variantLayers`) and this state only remembers the user's per-job
 * selection, defaulting to the Existing layer.
 */
export class VariantLayerStateImpl {
	private selectedByTag: ObservableMap<string, string> = observable.map<string, string>({}, { deep: false });

	constructor() {
		makeObservable(this);
	}

	/** Master token: the variant/verify layer feature is enabled at all (any page). */
	featureEnabled(): boolean {
		return Services.Token.has(IkeToken.FEATURE_VERIFY_LAYERS);
	}

	/** Big-map token: also show the layer selector on the /map jobs list. Implies {@link featureEnabled}. */
	bigMapEnabled(): boolean {
		return this.featureEnabled() && Services.Token.has(IkeToken.FEATURE_VERIFY_LAYERS_BIGMAP);
	}

	layerTree(tagId: string | null | undefined): VariantLayerNode | undefined {
		if (tagId == null) {
			return undefined;
		}
		return AppStore.TagSummary.get(tagId)?.variantLayers;
	}

	/**
	 * True when the job has at least one non-Existing layer AND the feature is enabled, i.e. a selector
	 * is worth showing. Gating the master token here makes every layer-aware consumer fall back to the
	 * legacy nested view when the feature is off, with no per-call token checks.
	 */
	hasLayers(tagId: string | null | undefined): boolean {
		if (!this.featureEnabled()) {
			return false;
		}
		const tree = this.layerTree(tagId);
		return tree != null && tree.children.length > 0;
	}

	selectedLayerId(tagId: string | null | undefined): string {
		if (tagId == null) {
			return EXISTING_LAYER_ID;
		}
		return this.selectedByTag.get(tagId) ?? EXISTING_LAYER_ID;
	}

	selectedNode(tagId: string | null | undefined): VariantLayerNode | undefined {
		return findLayer(this.layerTree(tagId), this.selectedLayerId(tagId));
	}

	@action select(tagId: string, layerId: string): void {
		this.selectedByTag.set(tagId, layerId);
	}

	/** Depth-first list of the job's layers with indentation depth, for the selector menu. */
	flatLayers(tagId: string | null | undefined): FlatVariantLayer[] {
		const flat: FlatVariantLayer[] = [];
		const tree = this.layerTree(tagId);
		if (tree != null) {
			flatten(tree, 0, flat);
		}
		return flat;
	}

	/**
	 * Revisions to show for the selected layer of a job, or undefined when the job has no layers (so
	 * callers keep their default, variant-free view unchanged).
	 */
	visibleRevisions(tagId: string | null | undefined): string[] | undefined {
		if (!this.hasLayers(tagId)) {
			return undefined;
		}
		return this.selectedNode(tagId)?.revisions ?? [];
	}

	/**
	 * The revisions in view for a job: the selected layer's when it has layers, otherwise the base
	 * poles (`tagSummary.revisions`). Used for counts, select-all and the list.
	 */
	effectiveRevisions(tagId: string | null | undefined): string[] {
		const layerRevisions = this.visibleRevisions(tagId);
		if (layerRevisions != null) {
			return layerRevisions;
		}
		if (tagId == null) {
			return [];
		}
		return AppStore.TagSummary.get(tagId)?.revisions ?? [];
	}
}

function flatten(node: VariantLayerNode, depth: number, out: FlatVariantLayer[]): void {
	out.push({ node, depth });
	for (const child of node.children) {
		flatten(child, depth + 1, out);
	}
}

function findLayer(node: VariantLayerNode | undefined, id: string): VariantLayerNode | undefined {
	if (node == null) {
		return undefined;
	}
	if (node.id === id) {
		return node;
	}
	for (const child of node.children) {
		const found = findLayer(child, id);
		if (found != null) {
			return found;
		}
	}
	return undefined;
}

export const VariantLayerState = new VariantLayerStateImpl();
